/**
 * Lo que la clínica configura para lo que ve y recibe el paciente: la cuenta
 * del ingreso, el enlace de pago de la reserva, los centros de reseña y el
 * texto de los WhatsApp. Se monta en Configuración.
 *
 * Nada de esto tiene valor de respaldo en el código: si un dato falta, la
 * función que lo usa no enseña ese bloque (sin IBAN no hay datos de
 * transferencia en «Sus pasos», sin centros no se ofrece «Pedir reseña»…) y
 * `AvisosConfiguracion` lo dice arriba de la pantalla.
 *
 * Los campos viven en el mismo formulario que el resto de Configuración: se
 * guardan con el botón «Guardar cambios» de siempre.
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { SectionCard } from './Common';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

/**
 * Lo que le falta a la configuración, dicho con lo que pasa si no se rellena
 * (GET /api/settings/avisos). `version` cambia al guardar: entonces se
 * vuelve a preguntar. Sin avisos, no pinta nada.
 */
export function AvisosConfiguracion({ version }) {
  const [avisos, setAvisos] = useState([]);
  useEffect(() => {
    let vivo = true;
    api.get('/api/settings/avisos')
      .then((r) => { if (vivo) setAvisos(Array.isArray(r?.data?.avisos) ? r.data.avisos : []); })
      .catch(() => { if (vivo) setAvisos([]); });
    return () => { vivo = false; };
  }, [version]);
  if (!avisos.length) return null;
  return (
    <div className="cp-card p-4 mb-6 border border-amber-300 bg-amber-50 text-amber-900" data-testid="settings-avisos">
      <p className="flex items-center gap-2 font-medium text-sm mb-2">
        <AlertTriangle className="w-4 h-4 shrink-0" /> Falta configurar
      </p>
      <ul className="list-disc pl-6 space-y-1 text-sm">
        {avisos.map((a, i) => (
          <li key={`${a.campo}-${i}`} data-testid={`settings-aviso-${a.campo}`}>{a.texto}</li>
        ))}
      </ul>
    </div>
  );
}

/** Un id para un centro nuevo: estable mientras exista, para que el diálogo
 *  de la reseña diga cuál eligió recepción aunque se reordenen. */
function idNuevo() {
  return `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

function CentrosResena({ centros, onChange, soloLectura }) {
  const lista = Array.isArray(centros) ? centros : [];
  const cambiar = (i, campo, valor) => onChange(lista.map((c, j) => (j === i ? { ...c, [campo]: valor } : c)));
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Cada centro con su nombre y su enlace para dejar la reseña en Google. Sin ninguno,
        no se ofrece «Pedir reseña»; con varios, recepción elige en cuál.
      </p>
      {lista.map((c, i) => (
        <div key={c.id || i} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2 items-end" data-testid={`settings-resena-centro-${i}`}>
          <div><Label>Nombre</Label><Input disabled={soloLectura} value={c.nombre || ''} onChange={(e) => cambiar(i, 'nombre', e.target.value)} data-testid={`settings-resena-nombre-${i}`} /></div>
          <div><Label>Enlace de reseña</Label><Input disabled={soloLectura} value={c.url || ''} placeholder="https://…" onChange={(e) => cambiar(i, 'url', e.target.value)} data-testid={`settings-resena-url-${i}`} /></div>
          {!soloLectura && (
            <Button type="button" variant="ghost" aria-label="Quitar centro" onClick={() => onChange(lista.filter((_, j) => j !== i))} data-testid={`settings-resena-quitar-${i}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      ))}
      {!soloLectura && (
        <Button type="button" variant="outline" onClick={() => onChange([...lista, { id: idNuevo(), nombre: '', url: '' }])} data-testid="settings-resena-anadir">
          <Plus className="w-4 h-4 mr-2" /> Añadir centro
        </Button>
      )}
    </div>
  );
}

/** Los lugares de quirófano: nombre → dirección. Son las opciones del
 *  desplegable «Lugar del quirófano» de la agenda, y la dirección sale en los
 *  mensajes al paciente. Se editan como filas; se guardan como objeto. */
function LugaresQuirofano({ lugares, onChange, soloLectura }) {
  const filas = Object.entries(lugares && typeof lugares === 'object' ? lugares : {});
  const guardar = (nuevas) => onChange(Object.fromEntries(nuevas));
  const cambiar = (i, pos, valor) => guardar(filas.map((f, j) => {
    if (j !== i) return f;
    const copia = [...f];
    copia[pos] = valor;
    return copia;
  }));
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Dónde se opera (el quirófano propio, un hospital…). Sin ninguno, la agenda de quirófano
        no ofrece lugar.
      </p>
      {filas.map(([nombre, direccion], i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2 items-end" data-testid={`settings-lugar-${i}`}>
          <div><Label>Nombre</Label><Input disabled={soloLectura} value={nombre} onChange={(e) => cambiar(i, 0, e.target.value)} data-testid={`settings-lugar-nombre-${i}`} /></div>
          <div><Label>Dirección</Label><Input disabled={soloLectura} value={direccion || ''} onChange={(e) => cambiar(i, 1, e.target.value)} data-testid={`settings-lugar-direccion-${i}`} /></div>
          {!soloLectura && (
            <Button type="button" variant="ghost" aria-label="Quitar lugar" onClick={() => guardar(filas.filter((_, j) => j !== i))} data-testid={`settings-lugar-quitar-${i}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      ))}
      {!soloLectura && (
        <Button type="button" variant="outline" disabled={filas.some(([n]) => !n.trim())}
          onClick={() => guardar([...filas, ['', '']])} data-testid="settings-lugar-anadir">
          <Plus className="w-4 h-4 mr-2" /> Añadir lugar
        </Button>
      )}
    </div>
  );
}

/**
 * El editor de las plantillas de WhatsApp. El catálogo (la genérica de cada
 * mensaje y sus marcadores) lo da el servidor; lo que se escribe aquí va a
 * `plantillas_whatsapp` del formulario. Un texto igual a la genérica no se
 * guarda como propio: así, si la genérica mejora, a la clínica le llega.
 */
function PlantillasWhatsApp({ propias, onChange, soloLectura }) {
  const [catalogo, setCatalogo] = useState([]);
  const [clave, setClave] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let vivo = true;
    api.get('/api/settings/plantillas-whatsapp')
      .then((r) => {
        if (!vivo) return;
        const lista = Array.isArray(r?.data?.plantillas) ? r.data.plantillas : [];
        setCatalogo(lista);
        setClave((c) => c || lista[0]?.clave || '');
      })
      .catch(() => { if (vivo) setError('No se han podido cargar las plantillas.'); });
    return () => { vivo = false; };
  }, []);
  if (error) return <p className="text-sm text-red-700">{error}</p>;
  const actual = catalogo.find((p) => p.clave === clave);
  if (!actual) return <p className="text-sm text-muted-foreground">Cargando…</p>;
  const mias = propias && typeof propias === 'object' ? propias : {};
  const propia = typeof mias[clave] === 'string' && mias[clave].trim() !== '';
  const texto = propia ? mias[clave] : actual.generica;
  const escribir = (valor) => {
    const siguiente = { ...mias };
    if (!valor.trim() || valor === actual.generica) delete siguiente[clave];
    else siguiente[clave] = valor;
    onChange(siguiente);
  };
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        El texto de cada mensaje. Lo que va entre llaves ({'{{FECHA}}'}, {'{{CLINICA}}'}…) se
        rellena solo con los datos de la cita y de Configuración; si falta un dato, su línea o su
        bloque no sale.
      </p>
      <div>
        <Label htmlFor="settings-plantilla-elegir">Mensaje</Label>
        <select
          id="settings-plantilla-elegir"
          className="mt-1 w-full rounded-md border border-border bg-white px-3 py-2 text-sm"
          value={clave}
          onChange={(e) => setClave(e.target.value)}
          data-testid="settings-plantilla-elegir"
        >
          {catalogo.map((p) => (
            <option key={p.clave} value={p.clave}>
              {p.titulo}{(typeof mias[p.clave] === 'string' && mias[p.clave].trim()) ? ' (propia)' : ''}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground mt-1">{actual.cuando}</p>
      </div>
      <Textarea
        rows={12}
        disabled={soloLectura}
        value={texto}
        onChange={(e) => escribir(e.target.value)}
        className="font-mono text-xs"
        data-testid="settings-plantilla-texto"
      />
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`rounded-full px-2 py-0.5 ${propia ? 'bg-emerald-100 text-emerald-800' : 'bg-muted text-muted-foreground'}`} data-testid="settings-plantilla-estado">
          {propia ? 'Texto propio de la clínica' : 'Texto genérico'}
        </span>
        {propia && !soloLectura && (
          <Button type="button" variant="outline" size="sm" onClick={() => escribir('')} data-testid="settings-plantilla-generica">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Volver al genérico
          </Button>
        )}
      </div>
      <div className="text-xs text-muted-foreground">
        <p className="font-medium mb-1">Marcadores de este mensaje:</p>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5">
          {actual.marcadores.map((m) => (
            <li key={m.marca}><code>{`{{${m.marca}}}`}</code> — {m.que}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function AjustesPaciente({ form, setF, soloLectura = false }) {
  const campo = (k, label, extra = {}) => (
    <div className={extra.ancho ? 'md:col-span-2' : ''}>
      <Label>{label}</Label>
      <Input disabled={soloLectura} value={form[k] || ''} placeholder={extra.placeholder}
        onChange={(e) => setF(k, e.target.value)} data-testid={`settings-${k}`} />
    </div>
  );
  return (
    <>
      <SectionCard title="Pagos de los pacientes">
        <p className="text-sm text-muted-foreground mb-3">
          La cuenta sale en el paso de pago de «Sus pasos» y en la confirmación de un retoque
          con coste. El enlace de pago de la reserva va en la confirmación de cirugía.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {campo('pago_iban', 'IBAN', { ancho: true })}
          {campo('pago_bic', 'BIC')}
          {campo('pago_titular', 'Titular de la cuenta')}
          {campo('stripe_reserva_qx_url', 'Enlace de pago de la reserva de quirófano', { ancho: true, placeholder: 'https://…' })}
        </div>
      </SectionCard>
      <SectionCard title="Textos y categorías propios">
        <p className="text-sm text-muted-foreground mb-3">
          El motivo sale en el presupuesto «sin IVA» y queda escrito en el evolutivo. El aviso del
          relleno previo va en el WhatsApp del presupuesto cuando la ficha lo marca; vacío, no se añade.
          Las categorías de honorarios salen como columnas en el panel de anestesistas.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {campo('motivo_exencion_iva', 'Motivo de exención de IVA', { ancho: true })}
          {campo('aviso_relleno_previo', 'Aviso de relleno previo (español)', { ancho: true })}
          {campo('aviso_relleno_previo_en', 'Aviso de relleno previo (inglés)', { ancho: true })}
          {campo('categorias_honorarios_anestesia', 'Categorías de honorarios del anestesista (además de módulo y externos; separadas por comas)', { ancho: true })}
        </div>
      </SectionCard>
      <SectionCard title="Lugares de quirófano">
        <LugaresQuirofano lugares={form.lugares_direcciones} onChange={(v) => setF('lugares_direcciones', v)} soloLectura={soloLectura} />
      </SectionCard>
      <SectionCard title="Reseñas de Google">
        <CentrosResena centros={form.resenas_centros} onChange={(v) => setF('resenas_centros', v)} soloLectura={soloLectura} />
      </SectionCard>
      <SectionCard title="Mensajes de WhatsApp al paciente" className="lg:col-span-2">
        <PlantillasWhatsApp propias={form.plantillas_whatsapp} onChange={(v) => setF('plantillas_whatsapp', v)} soloLectura={soloLectura} />
      </SectionCard>
    </>
  );
}
